/**
 * TubeForge Help Center — article data.
 *
 * Articles are typed block objects (not raw HTML) so the renderer
 * emits clean semantic HTML for SEO. Each block maps to a specific
 * HTML element: h2 → <h2>, p → <p>, ul → <ul><li>…, tip → styled
 * callout, plan → plan-tier badge list.
 */

export type ArticleBlock =
  | { type: "p";    text: string }
  | { type: "h2";   text: string }
  | { type: "h3";   text: string }
  | { type: "ul";   items: string[] }
  | { type: "ol";   items: string[] }
  | { type: "tip";  text: string }
  | { type: "warn"; text: string }
  | { type: "plan"; tiers: Array<"free" | "starter" | "pro" | "agency">; text: string }
  | { type: "steps"; items: Array<{ title: string; text: string }> };

export interface HelpArticle {
  slug: string;
  category: string;
  title: string;
  description: string;
  publishedAt: string;  // ISO date for JSON-LD
  modifiedAt?: string;  // Set when an article is updated; falls back to publishedAt
  body: ArticleBlock[];
}

export interface HelpCategory {
  slug: string;
  label: string;
  icon: string; // Icon name from primitives.tsx
  description: string;
}

// ── Categories ────────────────────────────────────────────────────────────────

export const HELP_CATEGORIES: HelpCategory[] = [
  {
    slug: "getting-started",
    label: "Getting Started",
    icon: "sparkle",
    description: "Set up your account and create your first video.",
  },
  {
    slug: "videos",
    label: "Videos",
    icon: "video",
    description: "Scripts, storyboards, rendering, and publishing.",
  },
  {
    slug: "series",
    label: "Series & Autopilot",
    icon: "layers",
    description: "Automate recurring video publishing on a schedule.",
  },
  {
    slug: "channels",
    label: "Channels",
    icon: "youtube",
    description: "Connect and manage your YouTube and TikTok channels.",
  },
  {
    slug: "assets",
    label: "Assets",
    icon: "image",
    description: "Upload images and use them in your videos.",
  },
  {
    slug: "strategy",
    label: "Strategy",
    icon: "stats",
    description: "AI-generated content strategy and video angle matrix.",
  },
  {
    slug: "billing",
    label: "Billing & Credits",
    icon: "creditCard",
    description: "Plans, credits, upgrades, and cancellations.",
  },
  {
    slug: "account",
    label: "Account & Team",
    icon: "settings",
    description: "Profile, team members, and the referral program.",
  },
  {
    slug: "troubleshooting",
    label: "Troubleshooting",
    icon: "help",
    description: "Fix common errors and stuck states.",
  },
];

// ── Articles ──────────────────────────────────────────────────────────────────

export const HELP_ARTICLES: HelpArticle[] = [

  // ── Getting Started ────────────────────────────────────────────────────────

  {
    slug: "what-is-tubeforge",
    category: "getting-started",
    title: "What is TubeForge?",
    description: "TubeForge is an AI-powered tool that takes you from a topic idea to a publish-ready faceless video in minutes — no camera, no editing software required.",
    publishedAt: "2025-01-15",
    body: [
      { type: "p", text: "TubeForge is an AI-powered video creation platform for faceless YouTube channels. You provide a topic and some context about your channel, and TubeForge generates a script, builds a visual storyboard, records an AI voiceover, adds captions, mixes background music, and renders a finished MP4 — all without you touching a camera or video editor." },
      { type: "h2", text: "What kinds of videos can I make?" },
      { type: "p", text: "TubeForge works best for informational, educational, and list-style videos that don't require on-screen talent. Common use cases include finance tips, health and wellness explainers, top-10 lists, motivational content, product reviews, and niche hobby channels." },
      { type: "h2", text: "How does the creation process work?" },
      { type: "steps", items: [
        { title: "Generate a script", text: "Describe your topic and content goals. The AI writes a structured script with a hook, body sections, and call-to-action in about 30 seconds." },
        { title: "Review the storyboard", text: "Approve the script, then TubeForge generates one AI image per scene. Edit any frame's prompt and regenerate until it looks right." },
        { title: "Render and publish", text: "Approve the storyboard and TubeForge assembles the video: voiceover, captions, music. Download the MP4 or publish straight to YouTube." },
      ]},
      { type: "h2", text: "What platforms does it publish to?" },
      { type: "p", text: "TubeForge can publish directly to YouTube. TikTok direct publishing is available on Pro and Agency plans. You can download the MP4 on any plan and upload it manually to any platform." },
      { type: "tip", text: "New to TubeForge? Start with the Getting Started guide to create your first video in a few steps." },
    ],
  },

  {
    slug: "creating-your-first-video",
    category: "getting-started",
    title: "Creating your first video",
    description: "A step-by-step walkthrough of creating, reviewing, and downloading your first TubeForge video.",
    publishedAt: "2025-01-15",
    body: [
      { type: "p", text: "This guide walks you through the entire video creation flow from start to finish. Your first video takes a few steps — most of the time is waiting for AI generation." },
      { type: "h2", text: "Step 1 — Start a new video" },
      { type: "p", text: "From the sidebar, click Videos, then New video. You'll be taken to the video creation form." },
      { type: "h2", text: "Step 2 — Fill in your content profile" },
      { type: "p", text: "The content profile tells the AI about your channel and audience. You only need to fill this in once — TubeForge saves it automatically for future videos." },
      { type: "ul", items: [
        "Channel niche — the topic area your channel covers (e.g. \"personal finance for millennials\")",
        "Target audience — who you're making videos for",
        "Call to action — what you want viewers to do after watching (subscribe, visit a link, etc.)",
        "Platform — YouTube, TikTok, Instagram, or general",
        "Video goal — grow audience, sell a product, build authority, entertain, or educate",
      ]},
      { type: "h2", text: "Step 3 — Choose a preset" },
      { type: "p", text: "Presets are visual style templates that control the look of your video — image style, caption font, color palette, and more. Pick one that fits your channel's aesthetic." },
      { type: "h2", text: "Step 4 — Add optional guidance" },
      { type: "p", text: "In the guidance field, you can give the AI a specific topic, angle, or instructions (e.g. \"focus on compound interest for beginners\"). This is optional — leaving it blank lets the AI choose a topic based on your content profile." },
      { type: "h2", text: "Step 5 — Generate" },
      { type: "p", text: "Click Generate. The AI will write your script in about 30 seconds. You'll be taken to the script review page automatically." },
      { type: "tip", text: "Your content profile is saved to your browser. You won't need to re-enter it for future videos on the same device." },
    ],
  },

  {
    slug: "understanding-credits",
    category: "getting-started",
    title: "Understanding credits",
    description: "Learn how TubeForge credits work, what actions cost credits, and how to check your balance.",
    publishedAt: "2025-01-15",
    body: [
      { type: "p", text: "Credits are TubeForge's usage currency. You spend credits when you generate scripts, render videos, and regenerate storyboard images. Your plan includes a monthly credit allocation that resets on your billing date." },
      { type: "h2", text: "Credit costs" },
      { type: "ul", items: [
        "Script generation — 5 credits",
        "Video render — 100 credits",
        "Image regeneration (per frame) — 5 credits",
      ]},
      { type: "h2", text: "How many credits does my plan include?" },
      { type: "p", text: "Each plan tier includes a monthly video allowance. The Free plan includes 2 videos per month, Starter includes 10, Pro includes 40, and Agency includes 150. Each video costs roughly 100 credits (one render) plus credits for any script generations and image regens." },
      { type: "h2", text: "Where can I see my credit balance?" },
      { type: "p", text: "Your current credit balance is shown in the top bar of the app. You can also see a full breakdown on the Billing page." },
      { type: "h2", text: "What happens when I run out of credits?" },
      { type: "p", text: "When your credits reach zero, you won't be able to generate new scripts or render videos until your balance resets on your billing date — or until you upgrade to a higher plan." },
      { type: "tip", text: "Script generation and image regens are cheap (5 credits each). The bulk of your credit spend is on video renders (100 credits). Use the script and storyboard review stages to get things right before rendering." },
    ],
  },

  {
    slug: "connecting-youtube-channel",
    category: "getting-started",
    title: "Connecting your YouTube channel",
    description: "How to connect a YouTube channel to TubeForge so you can publish videos directly — including required permissions and troubleshooting tips.",
    publishedAt: "2025-01-15",
    body: [
      { type: "p", text: "Connecting a YouTube channel allows you to publish finished videos directly to YouTube without leaving TubeForge. You can also enable scheduled publishing on Pro and Agency plans." },
      { type: "plan", tiers: ["starter", "pro", "agency"], text: "YouTube publishing requires a Starter plan or higher. Free plan users can download MP4s but cannot publish directly." },
      { type: "h2", text: "How to connect" },
      { type: "ol", items: [
        "Go to Channels in the sidebar.",
        "Click Connect channel and select YouTube.",
        "You'll be redirected to Google's OAuth consent screen. Sign in with the Google account that owns your YouTube channel.",
        "If you have multiple YouTube channels on one account, select the correct one during the OAuth flow.",
        "Grant the requested permissions. TubeForge needs permission to upload videos and manage your channel.",
        "You'll be redirected back to TubeForge. Your channel will appear in the Channels list within a few seconds.",
      ]},
      { type: "h2", text: "Required permissions" },
      { type: "p", text: "TubeForge requests permission to upload videos and read your channel information. TubeForge does not request permission to delete videos, access your email, or modify your Google account settings." },
      { type: "h2", text: "How many channels can I connect?" },
      { type: "ul", items: [
        "Starter — 1 channel",
        "Pro — 3 channels",
        "Agency — 10 channels",
      ]},
      { type: "h2", text: "Disconnecting a channel" },
      { type: "p", text: "To disconnect a channel, go to Channels, open the channel detail page, and click Disconnect. This revokes TubeForge's access. Videos already published will remain on YouTube." },
      { type: "h2", text: "Troubleshooting connection issues" },
      { type: "ul", items: [
        "Make sure you're signing in with the correct Google account",
        "If the redirect back to TubeForge fails, close the tab and try again from Channels → Connect",
        "If you revoke access from Google's security settings without disconnecting in TubeForge first, the channel will show as 'connection error' — reconnect it using the same steps above",
      ]},
      { type: "warn", text: "If you revoke access from Google's security settings without disconnecting in TubeForge first, the channel will show as \"connection error\" in your channels list. Reconnect it using the steps above." },
    ],
  },

  // ── Videos ─────────────────────────────────────────────────────────────────

  {
    slug: "how-script-generation-works",
    category: "videos",
    title: "How script generation works",
    description: "Understand how the AI writes your video script, what inputs it uses, and how to get better results.",
    publishedAt: "2025-01-20",
    body: [
      { type: "p", text: "Script generation is the first step of the video pipeline. The AI uses your content profile, the selected preset, and any optional guidance you provide to write a structured script with three sections: a hook, body paragraphs, and a call-to-action." },
      { type: "h2", text: "What the AI uses to write your script" },
      { type: "ul", items: [
        "Channel niche and target audience from your content profile",
        "Your video goal (grow audience, sell product, build authority, entertain, educate)",
        "Your preferred hook style (question, bold claim, story, controversy, statistic, or AI's choice)",
        "The selected preset's topic and style guidelines",
        "Any free-text guidance you entered in the guidance field",
      ]},
      { type: "h2", text: "Script structure" },
      { type: "p", text: "Every script has three parts:" },
      { type: "ul", items: [
        "Hook — the opening line designed to grab attention immediately",
        "Body paragraphs — the main content, broken into sections that become individual video scenes",
        "Call-to-action — the closing instruction (subscribe, visit a link, comment, etc.)",
      ]},
      { type: "h2", text: "Getting better results" },
      { type: "ul", items: [
        "A specific, detailed guidance field produces more targeted scripts than a vague topic",
        "If the AI picks a topic you don't like, use the \"Regenerate\" button rather than editing heavily from scratch",
        "Fill out all content profile fields — the more context the AI has, the more on-brand the script",
      ]},
      { type: "tip", text: "Generation takes about 30 seconds. If it takes longer than 2 minutes, refresh the page — a rare timeout may have occurred." },
    ],
  },

  {
    slug: "editing-regenerating-script",
    category: "videos",
    title: "Editing and regenerating your script",
    description: "How to edit script text inline, change the tone, and regenerate individual sections.",
    publishedAt: "2025-01-20",
    body: [
      { type: "p", text: "The script review page lets you read, edit, and approve your script before any images are generated. Nothing moves forward until you click Approve." },
      { type: "h2", text: "Inline editing" },
      { type: "p", text: "Click any paragraph or section to edit it directly. You can rewrite sentences, change word choices, or restructure entire sections. Your edits are saved automatically." },
      { type: "h2", text: "Regenerating the whole script" },
      { type: "p", text: "If you want a completely different script, click Regenerate. You can optionally select a tone modifier before regenerating:" },
      { type: "ul", items: [
        "Punchier — shorter sentences, more energy, stronger language",
        "Longer — more detail, more body paragraphs",
        "Shorter — tighter script, fewer words",
        "More casual — conversational, relaxed tone",
        "More serious — formal, authoritative tone",
      ]},
      { type: "warn", text: "Regenerating the script costs 5 credits. The original script is replaced and cannot be recovered, so copy any sections you want to keep before regenerating." },
      { type: "h2", text: "Word count and duration" },
      { type: "p", text: "The script review page shows a live word count and an estimated video duration. Duration is calculated at roughly 130 words per minute for the AI voiceover. Use this to stay within your plan's maximum video length." },
      { type: "h2", text: "Approving the script" },
      { type: "p", text: "When you're happy with the script, click Approve Script. TubeForge will immediately begin generating your storyboard images." },
    ],
  },

  {
    slug: "storyboard-review",
    category: "videos",
    title: "The storyboard review",
    description: "How to review, edit, and approve your video storyboard before rendering.",
    publishedAt: "2025-01-20",
    body: [
      { type: "p", text: "The storyboard is a grid of AI-generated images — one per script segment. Frames appear in real time as they are generated. You review each frame, make edits, and approve before the final render begins." },
      { type: "h2", text: "Editing a frame" },
      { type: "p", text: "Click any frame to open the edit panel. You'll see the AI prompt that was used to generate that image. Edit the prompt and click Regenerate to generate a new image for that frame. Regeneration costs 5 credits per frame." },
      { type: "h2", text: "Bulk regeneration" },
      { type: "p", text: "To regenerate multiple frames at once, hold Shift (or use the checkboxes on mobile) to select multiple frames, then click Regenerate selected. Each selected frame costs 5 credits." },
      { type: "h2", text: "Caption settings" },
      { type: "p", text: "At the top of the storyboard page you can set the caption font and caption color. These apply to all captions in the final video. Choose a font and color that contrasts well with your video's imagery." },
      { type: "h2", text: "Storyboard variants" },
      { type: "plan", tiers: ["pro", "agency"], text: "Pro and Agency plans generate 2 storyboard variants per video. You can switch between them using the variant selector at the top of the storyboard page." },
      { type: "h2", text: "Approving the storyboard" },
      { type: "p", text: "When all frames look good, click Approve Storyboard. This locks the storyboard and starts the render queue. The render progress page will open automatically." },
      { type: "tip", text: "You can leave the storyboard page and come back — your progress is saved. The images you've already regenerated are not lost." },
    ],
  },

  {
    slug: "render-progress",
    category: "videos",
    title: "Render progress and stages",
    description: "What each render stage means — generating clips, voiceover, rendering, and complete — plus what to do if a stage gets stuck for more than 10 minutes.",
    publishedAt: "2025-01-20",
    body: [
      { type: "p", text: "After you approve the storyboard, TubeForge assembles the final video automatically. The render progress page shows each stage in real time via a live connection." },
      { type: "h2", text: "Render stages" },
      { type: "steps", items: [
        { title: "Generating clips", text: "Individual video clips are created for each scene by combining the storyboard image with timing data from the script." },
        { title: "Voiceover generating", text: "The AI reads your script aloud. OpenAI TTS is used on Free and Starter plans; ElevenLabs is used on Pro; ElevenLabs multilingual is used on Agency." },
        { title: "Rendering", text: "All clips are assembled, the voiceover is synced to each scene, word-level captions are aligned, and background music is mixed in at a level below the voice." },
        { title: "Complete", text: "The final MP4 is ready. You can download it or publish it to YouTube." },
      ]},
      { type: "h2", text: "How long does rendering take?" },
      { type: "p", text: "Most videos render in 2–5 minutes. Longer videos (close to the plan's maximum length) can take up to 10 minutes. Render time depends on server load." },
      { type: "h2", text: "If the render gets stuck" },
      { type: "p", text: "If a stage shows no progress for more than 10 minutes, the render may have encountered an error. Refresh the page — if the status shows \"Failed\", see the Render errors troubleshooting article. You will not be charged credits for a failed render." },
    ],
  },

  {
    slug: "downloading-video",
    category: "videos",
    title: "Downloading your video",
    description: "How to download your finished TubeForge video as an MP4, available on all plans — 16:9 landscape for YouTube long-form or 9:16 vertical for Shorts.",
    publishedAt: "2025-01-20",
    body: [
      { type: "p", text: "Once a video reaches the Complete status, you can download it as an MP4 from the video detail page." },
      { type: "h2", text: "How to download" },
      { type: "ol", items: [
        "Go to Videos in the sidebar.",
        "Click the video you want to download.",
        "On the video detail page, click the Download button.",
        "The MP4 will download to your browser's default downloads folder.",
      ]},
      { type: "h2", text: "Video formats" },
      { type: "p", text: "Videos are rendered as MP4 (H.264). The aspect ratio depends on the format you selected when creating the video:" },
      { type: "ul", items: [
        "16:9 — standard landscape format for YouTube long-form",
        "9:16 — vertical format for YouTube Shorts, TikTok, and Instagram Reels",
      ]},
      { type: "tip", text: "Downloaded videos include the AI voiceover, word-level captions, and background music already mixed in. The file is ready to upload to any platform." },
    ],
  },

  {
    slug: "publishing-to-youtube",
    category: "videos",
    title: "Publishing to YouTube",
    description: "How to publish your finished video directly to YouTube from TubeForge.",
    publishedAt: "2025-01-20",
    body: [
      { type: "plan", tiers: ["starter", "pro", "agency"], text: "YouTube publishing requires a Starter plan or higher and at least one connected YouTube channel." },
      { type: "h2", text: "Instant publish" },
      { type: "p", text: "On the video detail page of a completed video, click Publish to YouTube. You'll be prompted to fill in:" },
      { type: "ul", items: [
        "Title — the YouTube video title (max 100 characters)",
        "Description — shown below the video on YouTube (max 5000 characters)",
        "Tags — comma-separated keywords",
        "Category — the YouTube content category",
        "Channel — which connected channel to publish to",
        "AI disclosure — enables the YouTube-required label for AI-generated content",
      ]},
      { type: "h2", text: "Scheduled publishing" },
      { type: "plan", tiers: ["pro", "agency"], text: "Scheduled publishing is available on Pro and Agency plans. Set a Publish at date and time to schedule the video instead of publishing immediately." },
      { type: "h2", text: "After publishing" },
      { type: "p", text: "Once published, the video's status changes to Published and a YouTube link appears on the video detail page. YouTube may take a few minutes to process the video before it appears on your channel." },
      { type: "warn", text: "You cannot unpublish or delete a YouTube video from TubeForge. To remove a video from YouTube, go to YouTube Studio." },
    ],
  },

  // ── Series & Autopilot ─────────────────────────────────────────────────────

  {
    slug: "what-is-a-series",
    category: "series",
    title: "What is a Series?",
    description: "Series let you automate recurring video creation and publishing on a schedule — TubeForge's autopilot mode.",
    publishedAt: "2025-02-01",
    body: [
      { type: "p", text: "A Series is a scheduled content plan that automatically creates and publishes videos for you on a repeating schedule. You set the topic area, style, and frequency — TubeForge handles the rest." },
      { type: "plan", tiers: ["starter", "pro", "agency"], text: "Series require a Starter plan or higher. Free plan users cannot create Series." },
      { type: "h2", text: "How autopilot works" },
      { type: "p", text: "When a Series is active, TubeForge automatically generates a script, builds a storyboard, renders the video, and publishes it to your connected YouTube channel on the schedule you set. You don't need to be online when it runs." },
      { type: "h2", text: "What you control" },
      { type: "ul", items: [
        "Topic area — the niche or subject the series covers",
        "Preset — the visual style used for every video in the series",
        "Frequency — how often new videos are created (daily, 3x/week, weekly, biweekly, monthly)",
        "Publish time — what time of day videos are published",
        "Connected channel — which YouTube channel the videos go to",
      ]},
      { type: "h2", text: "Series limits by plan" },
      { type: "ul", items: [
        "Starter — 1 active Series",
        "Pro — 3 active Series",
        "Agency — 10 active Series",
      ]},
      { type: "tip", text: "Series videos still count against your monthly video credit allowance. A weekly Series creates ~4 videos per month." },
    ],
  },

  {
    slug: "creating-a-series",
    category: "series",
    title: "Creating a Series",
    description: "How to create and configure an autopilot Series in TubeForge — choose a preset, content profile, publishing frequency, and connected YouTube channel.",
    publishedAt: "2025-02-01",
    body: [
      { type: "p", text: "Series are created from the Series page in the sidebar. Walk through the setup form to configure your content schedule." },
      { type: "h2", text: "Series setup" },
      { type: "ol", items: [
        "Go to Series in the sidebar and click New Series.",
        "Name your series — this is for your reference only and won't appear on YouTube.",
        "Choose a preset to define the visual style.",
        "Set your content profile for this series (can differ from your default).",
        "Choose a publishing frequency.",
        "Select the connected YouTube channel to publish to.",
        "Click Create Series.",
      ]},
      { type: "h2", text: "Activating the series" },
      { type: "p", text: "New series start in a paused state. Toggle it to Active to start the autopilot schedule. The first video will be queued shortly after activation." },
      { type: "tip", text: "Before activating a series, create one manual video with the same preset to verify the visual style looks right for your channel." },
    ],
  },

  {
    slug: "pausing-resuming-series",
    category: "series",
    title: "Pausing and resuming a Series",
    description: "How to pause, resume, or permanently cancel a TubeForge Series, and what happens to in-progress videos and published content when you do.",
    publishedAt: "2025-02-01",
    body: [
      { type: "p", text: "You can pause or resume a Series at any time from the Series detail page or the Series list." },
      { type: "h2", text: "Pausing a Series" },
      { type: "p", text: "Click the Pause button on a Series. The schedule stops immediately. Any video currently in progress will finish rendering and publish as normal — pausing only prevents new videos from being queued." },
      { type: "h2", text: "Resuming a Series" },
      { type: "p", text: "Click Resume on a paused Series. The next video will be queued according to the original schedule." },
      { type: "h2", text: "Cancelling a Series" },
      { type: "p", text: "To permanently cancel a Series, open the Series detail page and click Cancel Series. Cancelled series cannot be reactivated. Videos already published to YouTube are not affected." },
      { type: "warn", text: "Cancelling a series is permanent. If you think you might want to run this series again, pause it instead of cancelling." },
    ],
  },

  {
    slug: "series-frequency-options",
    category: "series",
    title: "Series frequency options",
    description: "The available publishing frequencies for Series and how they affect your monthly video usage.",
    publishedAt: "2025-02-01",
    body: [
      { type: "p", text: "When creating or editing a Series, you set how often new videos are automatically created and published." },
      { type: "h2", text: "Available frequencies" },
      { type: "ul", items: [
        "Daily — 1 video per day (~30 videos/month)",
        "3 times per week — ~12 videos/month",
        "Weekly — ~4 videos/month",
        "Biweekly — ~2 videos/month",
        "Monthly — 1 video/month",
      ]},
      { type: "h2", text: "Impact on your credit allowance" },
      { type: "p", text: "Each video in a Series counts against your plan's monthly video allowance just like a manually created video. A daily series requires a Pro or Agency plan — those are the only plans with enough monthly videos to support daily publishing." },
      { type: "tip", text: "Agency plan (150 videos/month) can support up to 5 daily series or a larger mix of weekly and biweekly schedules." },
    ],
  },

  // ── Channels ───────────────────────────────────────────────────────────────

  {
    slug: "connecting-tiktok",
    category: "channels",
    title: "Connecting TikTok",
    description: "How to connect a TikTok account to TubeForge for direct publishing, including Pro/Agency plan requirements, OAuth steps, and vertical 9:16 format requirements.",
    publishedAt: "2025-02-05",
    body: [
      { type: "plan", tiers: ["pro", "agency"], text: "TikTok direct publishing requires a Pro or Agency plan." },
      { type: "p", text: "Connecting TikTok allows you to publish 9:16 vertical videos directly to your TikTok account from TubeForge." },
      { type: "h2", text: "Steps to connect" },
      { type: "ol", items: [
        "Go to Channels in the sidebar.",
        "Click Connect channel.",
        "Select TikTok from the provider list.",
        "You'll be redirected to TikTok's authorization page. Log in to your TikTok account.",
        "Accept the requested permissions.",
        "You'll be redirected back to TubeForge.",
      ]},
      { type: "h2", text: "TikTok video requirements" },
      { type: "p", text: "TikTok publishing only works with videos in 9:16 (vertical) format. When creating a video for TikTok, select Shorts/Vertical format on the new video form." },
      { type: "warn", text: "TikTok's API has stricter content policies than YouTube. Videos with certain types of content may be rejected by TikTok at upload time." },
    ],
  },

  {
    slug: "scheduled-publishing",
    category: "channels",
    title: "Scheduled publishing",
    description: "How to schedule a TubeForge video to publish at a future date and time on YouTube, available on Pro and Agency plans, and how to change a scheduled time.",
    publishedAt: "2025-02-05",
    body: [
      { type: "plan", tiers: ["pro", "agency"], text: "Scheduled publishing requires a Pro or Agency plan." },
      { type: "p", text: "Instead of publishing a video immediately, you can schedule it to go live at a specific date and time." },
      { type: "h2", text: "How to schedule a publish" },
      { type: "ol", items: [
        "Open a completed video.",
        "Click Publish to YouTube.",
        "Fill in the title, description, and tags as normal.",
        "Toggle the Publish at option.",
        "Pick a date and time.",
        "Click Schedule.",
      ]},
      { type: "p", text: "The video will be uploaded to YouTube immediately as a private video and set to go public at the scheduled time. You'll see a Scheduled status on the video detail page." },
      { type: "h2", text: "Changing or cancelling a schedule" },
      { type: "p", text: "To change the publish time, go to YouTube Studio and edit the video's visibility settings there. TubeForge does not currently support rescheduling from within the app." },
    ],
  },

  // ── Assets ─────────────────────────────────────────────────────────────────

  {
    slug: "uploading-assets",
    category: "assets",
    title: "Uploading assets",
    description: "How to upload images to your TubeForge asset library, supported file types and size limits, and how to attach assets to a video for AI storyboard context.",
    publishedAt: "2025-02-10",
    body: [
      { type: "p", text: "Assets are images you upload to TubeForge for use in your videos. When you include assets in a video, the AI can use them as reference material when generating storyboard frames." },
      { type: "h2", text: "Uploading a file" },
      { type: "ol", items: [
        "Go to Assets in the sidebar.",
        "Click Upload and select a file from your device.",
        "The file will upload and appear in your asset library.",
      ]},
      { type: "h2", text: "Supported file types" },
      { type: "ul", items: [
        "Images: JPEG, PNG, WebP, GIF",
        "The maximum file size is 20 MB per file.",
      ]},
      { type: "h2", text: "Using assets in a video" },
      { type: "p", text: "On the new video form, you'll see an Assets section where you can select up to 10 assets to attach to the video. The AI uses these as visual context when generating storyboard images." },
      { type: "h2", text: "AI analysis" },
      { type: "p", text: "When you upload an image, TubeForge runs AI analysis on it to understand its content. This analysis is used to improve prompt generation for storyboard frames that reference the asset. Analysis typically completes within 30 seconds of upload." },
    ],
  },

  {
    slug: "importing-asset-from-url",
    category: "assets",
    title: "Importing an asset from a URL",
    description: "How to import an image into your asset library directly from a web URL.",
    publishedAt: "2025-02-10",
    body: [
      { type: "p", text: "Instead of uploading a file from your device, you can import an image directly from a URL." },
      { type: "h2", text: "How to import from URL" },
      { type: "ol", items: [
        "Go to Assets in the sidebar.",
        "Click Import from URL.",
        "Paste the full URL of the image (must be a direct image URL ending in .jpg, .png, .webp, etc.).",
        "Click Import. TubeForge will download the image and add it to your library.",
      ]},
      { type: "warn", text: "Only import images you have the right to use. TubeForge does not check image licensing." },
      { type: "h2", text: "Troubleshooting" },
      { type: "ul", items: [
        "Make sure the URL points directly to an image file, not a webpage",
        "Some URLs require authentication and cannot be imported — download the file and upload it instead",
        "Images larger than 20 MB cannot be imported",
      ]},
    ],
  },

  // ── Strategy ───────────────────────────────────────────────────────────────

  {
    slug: "what-is-the-strategy-tool",
    category: "strategy",
    title: "What is the Strategy tool?",
    description: "How TubeForge's Strategy tool generates a content matrix to plan your channel's video topics.",
    publishedAt: "2025-02-15",
    body: [
      { type: "p", text: "The Strategy tool analyzes your content profile and generates a complete content strategy: audience segments, messaging angles, and a matrix of video ideas ranked by priority." },
      { type: "h2", text: "What Strategy generates" },
      { type: "ul", items: [
        "Business analysis — your core value proposition, brand voice, and competitive position",
        "Audience segments — distinct groups in your target audience with different pain points and motivations",
        "Messaging angles — different ways to frame your content depending on the audience's awareness stage",
        "Content matrix — a grid pairing each audience segment with each angle, with a specific video concept and priority score",
        "Publishing strategy — recommendations for posting frequency and content mix",
      ]},
      { type: "h2", text: "How to generate a strategy" },
      { type: "ol", items: [
        "Go to Strategy in the sidebar.",
        "Review or update your content profile.",
        "Click Analyze. Generation takes about 60–90 seconds.",
        "Review the results and select the video concepts you want to produce.",
        "Click Create selected videos to turn chosen matrix cells into drafts.",
      ]},
      { type: "tip", text: "Strategies are saved automatically. You can return to a previous strategy from the Strategy page at any time." },
    ],
  },

  {
    slug: "creating-videos-from-strategy",
    category: "strategy",
    title: "Creating videos from a strategy",
    description: "How to select strategy matrix cells and turn them into TubeForge video drafts — including how to read priority scores to pick the highest-value topics first.",
    publishedAt: "2025-02-15",
    body: [
      { type: "p", text: "Once a strategy is generated, you can select specific matrix cells and convert them into video drafts with one click." },
      { type: "h2", text: "Selecting matrix cells" },
      { type: "p", text: "The content matrix is a grid where each row is an audience segment and each column is a messaging angle. Each cell contains a specific video concept. Click cells to select or deselect them. The priority score (1–3) indicates how high-value TubeForge thinks that combination is for your goals." },
      { type: "h2", text: "Creating videos from selected cells" },
      { type: "p", text: "After selecting one or more cells, click Create selected videos. TubeForge creates a new video draft for each selected cell, pre-filled with the relevant guidance from the strategy. You'll find these drafts in your Videos list." },
      { type: "tip", text: "Start with the highest-priority cells (priority 3). These represent the combinations where your audience's pain point and your message align most strongly." },
    ],
  },

  // ── Billing & Credits ──────────────────────────────────────────────────────

  {
    slug: "plan-comparison",
    category: "billing",
    title: "Plan comparison",
    description: "A detailed comparison of TubeForge's Free, Starter, Pro, and Agency plans.",
    publishedAt: "2025-01-15",
    body: [
      { type: "p", text: "TubeForge offers four plans. Here's a full breakdown of what's included in each." },
      { type: "h2", text: "Free Trial" },
      { type: "ul", items: [
        "2 videos per month",
        "Up to 3 minutes per video",
        "1 image regeneration per segment",
        "OpenAI voiceover",
        "Download MP4",
        "No YouTube publishing",
        "No series",
        "1 team seat",
      ]},
      { type: "h2", text: "Starter — $29/month" },
      { type: "ul", items: [
        "10 videos per month",
        "Up to 5 minutes per video",
        "2 image regenerations per segment",
        "OpenAI voiceover",
        "1 connected channel",
        "YouTube instant publish",
        "1 active Series",
        "1 team seat",
      ]},
      { type: "h2", text: "Pro — $79/month" },
      { type: "ul", items: [
        "40 videos per month",
        "Up to 15 minutes per video",
        "5 image regenerations per segment",
        "ElevenLabs voiceover (higher quality)",
        "2 storyboard variants per video",
        "3 connected channels",
        "YouTube instant and scheduled publish",
        "3 active Series",
        "1 team seat",
      ]},
      { type: "h2", text: "Agency — $249/month" },
      { type: "ul", items: [
        "150 videos per month",
        "Unlimited image regenerations",
        "ElevenLabs multilingual voiceover",
        "2 storyboard variants per video",
        "10 connected channels",
        "YouTube instant and scheduled publish",
        "10 active Series",
        "5 team seats",
      ]},
    ],
  },

  {
    slug: "upgrading-downgrading",
    category: "billing",
    title: "Upgrading or downgrading your plan",
    description: "How to upgrade or downgrade your TubeForge subscription, what happens to connected channels and active Series on downgrade, and how billing proration works.",
    publishedAt: "2025-01-15",
    body: [
      { type: "h2", text: "Upgrading" },
      { type: "p", text: "Go to Billing in the sidebar and click Upgrade. Select the new plan and complete the checkout. Your new plan takes effect immediately and you'll be charged the prorated difference for the remaining billing period." },
      { type: "h2", text: "Downgrading" },
      { type: "p", text: "Go to Billing and click Change plan. Select the lower plan. The downgrade takes effect at the end of your current billing period — you keep your current plan's features until then." },
      { type: "h2", text: "What happens to connected channels when downgrading?" },
      { type: "p", text: "If your new plan supports fewer channels than you currently have connected, you'll be asked to disconnect the excess channels before the downgrade goes through. For example, downgrading from Pro (3 channels) to Starter (1 channel) requires you to disconnect 2 channels first." },
      { type: "h2", text: "What happens to active series when downgrading?" },
      { type: "p", text: "If your new plan has a lower Series limit, your oldest series will be paused automatically until you are within the new limit." },
    ],
  },

  {
    slug: "cancellation-refunds",
    category: "billing",
    title: "Cancellation and refunds",
    description: "How to cancel your TubeForge subscription and TubeForge's refund policy.",
    publishedAt: "2025-01-15",
    body: [
      { type: "h2", text: "Cancelling your subscription" },
      { type: "p", text: "Go to Billing in the sidebar and click Cancel subscription. Your subscription will remain active until the end of the current billing period. After that, your account moves to the Free plan." },
      { type: "h2", text: "What happens after cancellation" },
      { type: "ul", items: [
        "All your videos, storyboards, and assets remain accessible",
        "Active Series are paused",
        "Connected channels beyond the Free limit (0) are disconnected",
        "You can continue to use the Free plan features",
      ]},
      { type: "h2", text: "Refund policy" },
      { type: "p", text: "TubeForge subscriptions are billed monthly and are generally non-refundable. If you believe you were charged in error, contact support within 7 days of the charge." },
      { type: "warn", text: "Unused credits do not roll over after cancellation and are not refunded." },
    ],
  },

  // ── Account & Team ─────────────────────────────────────────────────────────

  {
    slug: "profile-and-settings",
    category: "account",
    title: "Profile and settings",
    description: "How to update your profile, change your password, and manage notification preferences.",
    publishedAt: "2025-02-01",
    body: [
      { type: "p", text: "Your account settings are available from the Settings link at the bottom of the sidebar." },
      { type: "h2", text: "Changing your display name or email" },
      { type: "p", text: "Go to Settings and update the Name or Email fields. Changes take effect immediately. If you change your email, a verification email will be sent to the new address." },
      { type: "h2", text: "Changing your password" },
      { type: "p", text: "In Settings, click Change password. Enter your current password and your new password. Passwords must be at least 8 characters." },
      { type: "h2", text: "Dark mode" },
      { type: "p", text: "TubeForge supports light and dark themes. Toggle the theme in Settings. Your preference is saved to your account and synced across devices." },
    ],
  },

  {
    slug: "inviting-team-members",
    category: "account",
    title: "Inviting team members",
    description: "How to invite team members to your TubeForge workspace on the Agency plan, what permissions team members have, and how to remove a member.",
    publishedAt: "2025-02-01",
    body: [
      { type: "plan", tiers: ["agency"], text: "Team seats are available on the Agency plan (5 seats). All other plans include 1 seat (the account owner only)." },
      { type: "h2", text: "Inviting a team member" },
      { type: "ol", items: [
        "Go to Settings.",
        "Scroll to the Team section.",
        "Click Invite member.",
        "Enter the email address of the person you want to invite.",
        "Click Send invite.",
      ]},
      { type: "p", text: "The invited person will receive an email with a link to accept the invitation. They'll need to create a TubeForge account if they don't have one." },
      { type: "h2", text: "Team member permissions" },
      { type: "p", text: "Team members can create and manage videos, series, channels, and assets. They cannot access billing, invite other members, or delete the workspace." },
      { type: "h2", text: "Removing a team member" },
      { type: "p", text: "In Settings under Team, click the remove button next to a team member's name. Their access is revoked immediately." },
    ],
  },

  {
    slug: "referral-program",
    category: "account",
    title: "Referral program",
    description: "How to find your TubeForge referral link, how rewards are earned when a referred user subscribes, and how to track clicks, signups, and conversions.",
    publishedAt: "2025-02-01",
    body: [
      { type: "p", text: "The TubeForge referral program lets you earn bonus credits when you refer new users who sign up and subscribe." },
      { type: "h2", text: "Finding your referral link" },
      { type: "p", text: "Go to Referrals in the sidebar to find your unique referral link. Share it with anyone you think would benefit from TubeForge." },
      { type: "h2", text: "How rewards work" },
      { type: "p", text: "When someone signs up using your referral link and starts a paid subscription, both you and the new user receive bonus credits added to your accounts automatically." },
      { type: "h2", text: "Tracking your referrals" },
      { type: "p", text: "The Referrals page shows how many people have clicked your link, how many have signed up, and how many have converted to paid plans." },
    ],
  },

  // ── Troubleshooting ────────────────────────────────────────────────────────

  {
    slug: "video-stuck-generating",
    category: "troubleshooting",
    title: "Video stuck in generating",
    description: "What to do if your video's script or storyboard is stuck in a generating state.",
    publishedAt: "2025-02-20",
    body: [
      { type: "p", text: "Script and storyboard generation usually complete within a few minutes. If a video has been stuck in a generating state for more than 10 minutes, something may have gone wrong." },
      { type: "h2", text: "Script stuck generating" },
      { type: "ol", items: [
        "Refresh the page. The generation may have completed and the page just hasn't updated.",
        "If the status is still \"Generating\" after a refresh, wait 5 more minutes.",
        "If it's been more than 15 minutes total, the generation likely failed silently. Go to the video and click Regenerate script. You will not be charged extra credits for this retry.",
      ]},
      { type: "h2", text: "Storyboard frames stuck generating" },
      { type: "p", text: "Storyboard images stream in one by one. If one or more frames shows a loading spinner for more than 5 minutes:" },
      { type: "ol", items: [
        "Refresh the page.",
        "If the frame is still blank, click it and use the Regenerate button for that individual frame.",
        "If many frames are stuck, use bulk-select to regenerate all blank frames at once.",
      ]},
      { type: "tip", text: "Image generation depends on third-party AI providers. Occasional slowdowns happen during periods of high demand." },
    ],
  },

  {
    slug: "youtube-publish-failed",
    category: "troubleshooting",
    title: "YouTube publish failed",
    description: "How to diagnose and fix a failed YouTube publish in TubeForge — covering expired channel connections, daily upload quota errors, and content policy rejections.",
    publishedAt: "2025-02-20",
    body: [
      { type: "p", text: "If a video shows a \"Publish failed\" status, the video did not make it to YouTube. The most common causes are an expired YouTube connection or a quota issue." },
      { type: "h2", text: "Check your channel connection" },
      { type: "ol", items: [
        "Go to Channels.",
        "Check if the channel shows a connection error or a re-authenticate prompt.",
        "If it does, click Reconnect and go through the OAuth flow again.",
        "After reconnecting, retry the publish from the video detail page.",
      ]},
      { type: "h2", text: "YouTube quota errors" },
      { type: "p", text: "YouTube limits how many videos can be uploaded per day per API key. If you publish a large number of videos in a single day, you may hit this limit. The quota resets at midnight Pacific time. Wait until the next day and retry." },
      { type: "h2", text: "Content policy rejection" },
      { type: "p", text: "YouTube may reject a video that violates its content policy. If the error message mentions content policy, review the video content against YouTube's Community Guidelines before retrying." },
      { type: "warn", text: "Credits are charged when a video is rendered, not when it's published. A failed publish does not result in a credit refund, but you can retry the publish for free." },
    ],
  },

  {
    slug: "render-errors",
    category: "troubleshooting",
    title: "Render errors",
    description: "What to do when a TubeForge render fails — how to read the error message, retry the render, and confirm your 100 render credits are refunded on failure.",
    publishedAt: "2025-02-20",
    body: [
      { type: "p", text: "If your video shows a \"Failed\" status on the render progress page, the render did not complete. Here's how to diagnose and recover." },
      { type: "h2", text: "Check the error message" },
      { type: "p", text: "The video detail page shows an error message when a render fails. Common errors:" },
      { type: "ul", items: [
        "\"Voiceover generation failed\" — retry the render; this is usually a temporary issue with the TTS provider",
        "\"Asset not found\" — one of the storyboard images couldn't be accessed; go back to the storyboard and regenerate the affected frames before retrying",
        "\"Video duration exceeds plan limit\" — your script is too long for your plan's maximum video length; shorten the script and re-render",
      ]},
      { type: "h2", text: "Retrying a failed render" },
      { type: "p", text: "On the video detail page, click Retry render. If the same error occurs again, contact support with the video ID from your browser's URL." },
      { type: "h2", text: "Credits and failed renders" },
      { type: "p", text: "Render credits (100 credits) are refunded automatically when a render fails. The refund appears in your credit balance within a few minutes of the failure." },
    ],
  },

  {
    slug: "storyboard-image-failed",
    category: "troubleshooting",
    title: "Storyboard image failed to generate",
    description: "Why a TubeForge storyboard frame may fail and how to fix it — edit the AI prompt, regenerate individually or in bulk, and what to do during a service outage.",
    publishedAt: "2025-02-20",
    body: [
      { type: "p", text: "Occasionally a storyboard frame will fail to generate and display an error state instead of an image. This is almost always a temporary issue with the image generation service." },
      { type: "h2", text: "Fixing a failed frame" },
      { type: "ol", items: [
        "Click the failed frame.",
        "The frame's prompt will be shown in the edit panel.",
        "Click Regenerate. A new image will be generated using the same prompt.",
        "If the frame fails again, try editing the prompt slightly — very long or complex prompts occasionally cause failures.",
      ]},
      { type: "h2", text: "If many frames are failing" },
      { type: "p", text: "If most or all frames are failing, the image generation service may be experiencing an outage. Wait 10–15 minutes and use bulk regeneration to retry all failed frames." },
      { type: "tip", text: "5 credits are charged per regeneration. If frames keep failing due to a service outage, contact support — failed-frame credits can be reviewed." },
    ],
  },
];

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Return all articles in a given category. */
export function getArticlesByCategory(category: string): HelpArticle[] {
  return HELP_ARTICLES.filter((a) => a.category === category);
}

/** Return a single article by slug, or null if not found. */
export function getArticleBySlug(slug: string): HelpArticle | null {
  return HELP_ARTICLES.find((a) => a.slug === slug) ?? null;
}

/** Return related articles (same category, excluding current). */
export function getRelatedArticles(article: HelpArticle, limit = 5): HelpArticle[] {
  return HELP_ARTICLES.filter(
    (a) => a.category === article.category && a.slug !== article.slug
  ).slice(0, limit);
}

/** Search articles by title and description (case-insensitive). */
export function searchArticles(query: string): HelpArticle[] {
  const q = query.toLowerCase().trim();
  if (!q) return [];
  return HELP_ARTICLES.filter(
    (a) =>
      a.title.toLowerCase().includes(q) ||
      a.description.toLowerCase().includes(q) ||
      a.category.toLowerCase().includes(q)
  );
}

/** Popular articles shown on the help index (hardcoded by slug). */
export const POPULAR_ARTICLE_SLUGS = [
  "creating-your-first-video",
  "understanding-credits",
  "how-script-generation-works",
  "storyboard-review",
  "connecting-youtube-channel",
  "plan-comparison",
];
